import { Storage } from 'aws-amplify';
import awsExports from '../aws-exports';

/**
 * Centralized attachment URL resolver.
 *
 * Background: attachments are uploaded with Amplify Storage (default "public" access
 * level, which stores objects under the `public/` prefix). Historically they were read
 * back with `Storage.get` (a presigned URL that always works for an authed user). Some
 * code paths were later switched to hardcoded direct public S3 URLs, which only work if
 * the object is truly public-read — causing 404s for some files (e.g. docx/mp4).
 *
 * This helper restores the reliable behavior with a built-in fallback:
 *   1. Try `Storage.get` (presigned URL).               <- correctness
 *   2. Fall back to a direct public S3 URL.             <- backstop / backward compat
 *
 * The bucket/region are read from aws-exports so the fallback URL is environment-correct
 * (instead of hardcoding the prod bucket like the existing inline code does).
 */

const BUCKET = awsExports?.aws_user_files_s3_bucket;
const REGION =
  awsExports?.aws_user_files_s3_bucket_region || awsExports?.aws_appsync_region;

/**
 * Percent-encode each path segment of an S3 key while preserving the `/`
 * separators. Object keys embed the original filename (e.g.
 * `attachments/1700000000000-My Report.docx`), so spaces and special characters
 * (`#`, `?`, `+`, `&`, …) MUST be encoded or S3 returns 404. The browser
 * auto-encodes when a URL is placed in `<img src>`, which is why image
 * attachments appeared to work while docx/mp4/pdf — fetched via fetch()/
 * window.open() that do NOT auto-encode — 404'd.
 * @param {string} key
 * @returns {string}
 */
export const encodeKey = (key) =>
  key
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');

/**
 * Build a direct public S3 URL for a storage key, ensuring the `public/` prefix.
 * @param {string} key
 * @returns {string|null}
 */
export const buildPublicUrl = (key) => {
  if (!key || !BUCKET || !REGION) return null;
  const cleanKey = key.startsWith('public/') ? key : `public/${key}`;
  return `https://${BUCKET}.s3.${REGION}.amazonaws.com/${encodeKey(cleanKey)}`;
};

/**
 * Normalize an attachment (string key, or object with .key/.url) to a raw key/URL string.
 * @param {string|{key?: string, url?: string}} attachment
 * @returns {string|null}
 */
const extractRaw = (attachment) => {
  if (!attachment) return null;
  if (typeof attachment === 'string') return attachment;
  return attachment.key || attachment.url || null;
};

/**
 * Resolve a usable URL for an attachment, preferring a presigned URL and falling back to
 * a direct public S3 URL. Returns null if nothing can be resolved.
 *
 * @param {string|{key?: string, url?: string}} attachment
 * @returns {Promise<string|null>}
 */
export const resolveAttachmentUrl = async (attachment) => {
  const raw = extractRaw(attachment);
  if (!raw) return null;

  // Already a fully-qualified URL → use as-is.
  if (/^https?:\/\//i.test(raw)) return raw;

  // Amplify's default "public" level prepends `public/`, so Storage.get expects the key
  // WITHOUT that prefix.
  const storageKey = raw.startsWith('public/')
    ? raw.slice('public/'.length)
    : raw;

  // 1) Presigned URL — works for authed users regardless of object-level public-read.
  try {
    const signed = await Storage.get(storageKey);
    if (signed) return signed;
  } catch (e) {
    // fall through to the public URL fallback
  }

  // 2) Direct public S3 URL fallback.
  return buildPublicUrl(raw);
};

export default resolveAttachmentUrl;
